package com.flowyride.feature.navigation.ui

import androidx.compose.foundation.Canvas
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import com.flowyride.core.domain.model.Maneuver
import com.flowyride.core.domain.model.ManeuverType
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

/**
 * The maneuver as an arrow, drawn: glyph fonts miss these symbols on the web.
 * Roundabouts show the exit's direction around a ring.
 */
@Composable
internal fun ManeuverIcon(maneuver: Maneuver?, color: Color, modifier: Modifier = Modifier) {
    Canvas(modifier) {
        val stroke = size.minDimension * 0.11f
        when (maneuver?.type) {
            ManeuverType.ARRIVE, ManeuverType.WAYPOINT -> drawFlag(color, stroke)
            ManeuverType.ROUNDABOUT -> drawRoundabout(maneuver.turnAngle ?: 0.0, color, stroke)
            ManeuverType.U_TURN -> drawUTurn(color, stroke)
            null, ManeuverType.STRAIGHT, ManeuverType.OTHER, ManeuverType.DEPART, ManeuverType.FERRY, ManeuverType.MERGE ->
                drawTurn(0.0, color, stroke)
            else -> drawTurn(maneuver.type.angle(), color, stroke)
        }
    }
}

/** Degrees, negative to the left. */
private fun ManeuverType.angle(): Double = when (this) {
    ManeuverType.TURN_LEFT -> -90.0
    ManeuverType.TURN_RIGHT -> 90.0
    ManeuverType.SHARP_LEFT -> -135.0
    ManeuverType.SHARP_RIGHT -> 135.0
    ManeuverType.BEAR_LEFT, ManeuverType.KEEP_LEFT, ManeuverType.EXIT_LEFT -> -40.0
    ManeuverType.BEAR_RIGHT, ManeuverType.KEEP_RIGHT, ManeuverType.EXIT_RIGHT -> 40.0
    else -> 0.0
}

/** A stem from the bottom, bending at the centre towards [angle]. */
private fun DrawScope.drawTurn(angle: Double, color: Color, stroke: Float) {
    val w = size.width
    val h = size.height
    val bend = Offset(w / 2, h * 0.5f)
    val radians = angle * PI / 180
    val length = h * 0.34f
    val tip = Offset(bend.x + (sin(radians) * length).toFloat(), bend.y - (cos(radians) * length).toFloat())
    val path = Path().apply {
        moveTo(w / 2, h * 0.92f)
        lineTo(bend.x, bend.y)
        lineTo(tip.x, tip.y)
    }
    drawPath(path, color, style = Stroke(stroke, cap = StrokeCap.Round, join = StrokeJoin.Round))
    drawArrowHead(tip, radians, color, stroke)
}

private fun DrawScope.drawUTurn(color: Color, stroke: Float) {
    val w = size.width
    val h = size.height
    val path = Path().apply {
        moveTo(w * 0.62f, h * 0.9f)
        lineTo(w * 0.62f, h * 0.4f)
        cubicTo(w * 0.62f, h * 0.12f, w * 0.3f, h * 0.12f, w * 0.3f, h * 0.4f)
        lineTo(w * 0.3f, h * 0.62f)
    }
    drawPath(path, color, style = Stroke(stroke, cap = StrokeCap.Round, join = StrokeJoin.Round))
    drawArrowHead(Offset(w * 0.3f, h * 0.66f), PI, color, stroke)
}

/** A ring with an entry from the bottom and the exit towards [angle] (0 = straight across). */
private fun DrawScope.drawRoundabout(angle: Double, color: Color, stroke: Float) {
    val w = size.width
    val h = size.height
    val center = Offset(w / 2, h * 0.48f)
    val radius = w * 0.2f
    drawCircle(color, radius, center, style = Stroke(stroke * 0.8f))
    drawLine(color, Offset(w / 2, h * 0.94f), Offset(w / 2, center.y + radius), stroke, StrokeCap.Round)
    val radians = angle * PI / 180
    val out = Offset(center.x + (sin(radians) * radius).toFloat(), center.y - (cos(radians) * radius).toFloat())
    val tip = Offset(center.x + (sin(radians) * radius * 2.1f).toFloat(), center.y - (cos(radians) * radius * 2.1f).toFloat())
    drawLine(color, out, tip, stroke, StrokeCap.Round)
    drawArrowHead(tip, radians, color, stroke)
}

private fun DrawScope.drawFlag(color: Color, stroke: Float) {
    val w = size.width
    val h = size.height
    drawLine(color, Offset(w * 0.32f, h * 0.9f), Offset(w * 0.32f, h * 0.12f), stroke, StrokeCap.Round)
    val flag = Path().apply {
        moveTo(w * 0.32f, h * 0.14f)
        lineTo(w * 0.8f, h * 0.28f)
        lineTo(w * 0.32f, h * 0.46f)
        close()
    }
    drawPath(flag, color)
}

private fun DrawScope.drawArrowHead(tip: Offset, radians: Double, color: Color, stroke: Float) {
    val size = stroke * 2.2f
    val back = radians + PI
    val left = back - PI / 5
    val right = back + PI / 5
    val head = Path().apply {
        moveTo(tip.x + (sin(radians) * stroke * 0.6f).toFloat(), tip.y - (cos(radians) * stroke * 0.6f).toFloat())
        lineTo(tip.x + (sin(left) * size).toFloat(), tip.y - (cos(left) * size).toFloat())
        lineTo(tip.x + (sin(right) * size).toFloat(), tip.y - (cos(right) * size).toFloat())
        close()
    }
    drawPath(head, color)
}
